/**
 * The catalogue detail's data rows (pure): facts, links, components, the
 * »Na tej strani« rail and the surface chip — the mapping from `WikiEntry`
 * to what the article shows, in the design's order. No status or note
 * blocks: the only chip is the green »Posodobljeno … nazaj« (docsTime.ts).
 */

import type { WikiEntry, WikiSection } from '../../shared/types/layerWiki.js';
import { formatLjubljanaTs } from '../../shared/utils/ljubljanaTime.js';
import { docsCopy } from './docsCopy.js';
import { isHttpUrl } from './docsHtml.js';
import { childrenOf, parentOf, type DocsCatalogue } from './docsModel.js';
import { viriSourcePath } from './docsRouter.js';
import { chipText } from './docsTime.js';
import { effectiveWeb, hasOwnDesktop, isPartOf, surfaceOf } from './wikiSurface.js';

export interface FactRow {
  label: string;
  value: string;
  href?: string;
  /** The href stays on this page (client-routed, no new tab) — the »Vir« row. */
  internal?: boolean;
  /** Hover text (the tile date behind the »King.Prostor web« row). */
  title?: string;
}

export interface LinkRow {
  label: string;
  url: string;
}

export interface CompRow {
  label: string;
  meta: string;
  chip: string;
}

export interface RailItem {
  href: string;
  label: string;
}

/** Whose words the description is, or the raw origin code when unknown. */
export function originLabel(origin: string): string {
  return docsCopy.origin[origin] ?? origin;
}

/**
 * The facts card, in Teo's order (2026-09-28): »Datum vira«, »Datum King.Prostor web«
 * (web tab, when the web serves the entry), »Datum King.Prostor CAD« (CAD tab only),
 * then »Vir« (publisher, credit line and licence in one row). The date rows never go
 * missing on their tab (Teo 2026-09-15): »Datum vira« — the publisher's date from the
 * import ledgers — and the KingCAD date — the King import — read »—« when the ledgers
 * hold none. No »Besedilo« row — `originText` reads beside »Uredi opis« for admins.
 */
export function buildFacts(e: WikiEntry, parent: WikiEntry | null, section: WikiSection = 'web'): FactRow[] {
  const rows: FactRow[] = [];

  // »Datum vira« on every entry — blank when the ledgers hold no vintage. A catalog
  // product (DOF, LiDAR) dates itself by »Leto snemanja« instead.
  if (e.vintage) {
    const range = e.mixedVintages && e.vintageMax && e.vintageMax !== e.vintage ? `${e.vintage} – ${e.vintageMax}` : e.vintage;
    rows.push({ label: docsCopy.facts.vintage, value: range });
  } else if (!e.acquisitionYears) {
    rows.push(unknownDateRow(docsCopy.facts.vintage));
  }
  if (e.acquisitionYears) rows.push({ label: docsCopy.facts.acquisition, value: e.acquisitionYears });

  // Two "served since" moments, one per product: the web date is when the last
  // tile build finished (or the import promote for live routes), the KingCAD
  // date is the import promote. The tile data date rides in the hover text.
  // A KingCAD layer entry has no web side (the tab says it).
  if (section !== 'cad') {
    const web = effectiveWeb(e, parent);
    if (web.servedAt) {
      const bits = [formatLjubljanaTs(web.servedAt, 'datetime')];
      if (web.state === 'behind') bits.push(docsCopy.tilesBehind);
      if (web.state === 'live') bits.push(docsCopy.tilesLive);
      const row: FactRow = { label: docsCopy.facts.web, value: bits.join(' · ') };
      if (web.tileDate) row.title = `${docsCopy.tilesDated}: ${formatLjubljanaTs(web.tileDate, 'date')}`;
      rows.push(row);
    } else if (web.state === 'live') {
      rows.push({ label: docsCopy.facts.web, value: docsCopy.tilesLive });
    } else if (web.state !== 'none') {
      rows.push(unknownDateRow(docsCopy.facts.web));
    }
  }

  // »Datum King.Prostor CAD« = the King import — on the CAD tab only (Teo 2026-09-28: "dont
  // show the cad datum on the web pages"): every KingCAD layer section and every product page
  // KingCAD alone serves; blank when no date is known.
  if (section === 'cad') {
    if (e.desktop.servedAt) {
      rows.push({ label: docsCopy.facts.desktop, value: formatLjubljanaTs(e.desktop.servedAt, 'datetime') });
    } else {
      rows.push(unknownDateRow(docsCopy.facts.desktop));
    }
  }

  // ONE »Vir« row, last (Teo 2026-09-28: "combine the vir/licenca/navedba vira rows into 1
  // row"): "<publisher>, <credit line> (<licence>)" — »Geodetska uprava Republike Slovenije,
  // © GURS (CC BY 4.0)«; a missing credit or licence just drops out. It links to the
  // publisher's section on Viri in licence (Teo 2026-09-08), which carries the licence link.
  if (e.source.name) {
    const licence = e.licence ?? e.source.licence;
    const bits = [e.source.name];
    if (e.source.attribution) bits.push(e.source.attribution);
    const value = `${bits.join(', ')}${licence ? ` (${licence})` : ''}`;
    rows.push({ label: docsCopy.facts.source, value, href: viriSourcePath(e.source.id), internal: true });
  }
  return rows;
}

/**
 * A PART of a product with no ledger of its own (no import, no vintage, no desktop
 * registration) is dated by its product (2026-09-28 — the five DRAJV layers each carry
 * only their web toggle; the umbrella owns the dataset, the archive and the tables): the
 * entry as shown carries the parent's dates, desktop side and, when its own tiles say
 * nothing, the parent's web state. Anything else is returned as is.
 */
export function inheritDates(e: WikiEntry, parent: WikiEntry | null): WikiEntry {
  if (!parent || !isPartOf(e)) return e;
  if (e.importedAt || e.vintage || e.desktop.servedAt || hasOwnDesktop(e)) return e;
  // The server calls a toggle without an archive of its own »live«; the tiles it toggles are
  // the product's, so its web row reads the product's build when its own carries no date.
  const web =
    !e.web.servedAt && !e.web.tileDate && (parent.web.servedAt || parent.web.tileDate)
      ? { ...e.web, state: parent.web.state, tileDate: parent.web.tileDate, builtAt: parent.web.builtAt, servedAt: parent.web.servedAt }
      : e.web;
  return {
    ...e,
    vintage: parent.vintage,
    vintageMax: parent.vintageMax,
    mixedVintages: parent.mixedVintages,
    importedAt: parent.importedAt,
    desktop: parent.desktop,
    web,
  };
}

/** A date row the ledgers cannot fill: »—« with the reason as hover text. */
function unknownDateRow(label: string): FactRow {
  return { label, value: docsCopy.facts.unknownDate, title: docsCopy.facts.unknownDateTitle };
}

/**
 * Whose words the text is (+ the 0569 edit stamp) — "opis sloja v KingCAD · urejeno 2026-09-08".
 * Since 2026-09-28 (Teo) this is NOT a row of the facts card: it reads beside »Uredi opis«, for
 * king admins only. Null when the origin is unknown.
 */
export function originText(e: WikiEntry): string | null {
  if (!e.descriptionOrigin) return null;
  // A hand edit on /docs stamps the date after whose words these are (0569).
  const bits = [originLabel(e.descriptionOrigin)];
  if (e.textEditedAt) bits.push(docsCopy.facts.edited(formatLjubljanaTs(e.textEditedAt, 'date')));
  return bits.join(' · ');
}

/** Publisher page, metadata record, then the seed's extra docs — http(s) only. */
export function buildLinks(e: WikiEntry): LinkRow[] {
  const links: LinkRow[] = [];
  if (isHttpUrl(e.publisherUrl)) links.push({ label: docsCopy.links.publisher, url: e.publisherUrl });
  if (isHttpUrl(e.metadataUrl)) links.push({ label: docsCopy.links.metadata, url: e.metadataUrl });
  for (const d of e.docs) {
    if (isHttpUrl(d.url) && d.label) links.push({ label: d.label, url: d.url });
  }
  return links;
}

/** The »Sestavni deli« rows — the section renders only with two or more. */
export function buildComponents(e: WikiEntry, generatedAt: string): CompRow[] {
  return e.components.map((c) => {
    const meta: string[] = [];
    if (c.vintage) meta.push(c.vintage);
    if (c.importedAt) meta.push(formatLjubljanaTs(c.importedAt, 'date'));
    if (c.count !== null) meta.push(docsCopy.sheets(c.count));
    if (c.parked) meta.push(docsCopy.parked);
    return {
      label: c.label ?? c.key,
      meta: meta.join(' · '),
      chip: chipText(c.importedAt ?? c.vintage, generatedAt),
    };
  });
}

/** True when the entry has any description text to anchor »Opis«. */
export function hasDescription(e: WikiEntry): boolean {
  return Boolean(e.description) || Boolean(e.descriptionCs);
}

/**
 * `admin`: the viewer is a king admin — »Zgodovina urejanja« follows. »Opis« is
 * always there: an entry without text renders the »Opis še ni napisan.« notice
 * under the same anchor (0584 — the KingCAD layers without a desktop text).
 */
export function buildRail(_e: WikiEntry, links: LinkRow[], comps: CompRow[], hasRelated = false, admin = false): RailItem[] {
  const rail: RailItem[] = [];
  rail.push({ href: '#sec-opis', label: docsCopy.katalog.rail.opis });
  rail.push({ href: '#sec-podatki', label: docsCopy.katalog.rail.podatki });
  if (links.length > 0) rail.push({ href: '#sec-povezave', label: docsCopy.katalog.rail.povezave });
  if (hasRelated) rail.push({ href: '#sec-povezani', label: docsCopy.katalog.rail.povezani });
  if (comps.length >= 2) rail.push({ href: '#sec-sestavni', label: docsCopy.katalog.rail.sestavni });
  if (admin) rail.push({ href: '#sec-zgodovina', label: docsCopy.katalog.rail.zgodovina });
  return rail;
}

// ---------------------------------------------------------------------------
// Relations between entries (Teo 2026-09-08): every `parentKey` link is one
// of three kinds, read off the CHILD — a »Posebni pogled« restyles its base
// layer, a computed layer (the GJI varovalni pasovi) is derived from its
// network, everything else is a part of an umbrella (GJI networks, ČÚZK
// children, the ARSO cone). The parent shows the same links the other way.
// A fourth kind since 0584, NOT through parentKey: a KingCAD layer entry names
// the product whose table it reads (`cad.productKey`), and the product lists
// its KingCAD layers (`cadLayers`).
// ---------------------------------------------------------------------------

export type RelationKind = 'part' | 'computed' | 'preset' | 'cad';

export function relationKind(child: WikiEntry): RelationKind {
  if (child.web.presets.length > 0) return 'preset';
  if (child.cadence === 'computed') return 'computed';
  return 'part';
}

export interface RelatedGroup {
  kind: RelationKind;
  entries: WikiEntry[];
}

export interface Relations {
  parent: { kind: RelationKind; entry: WikiEntry } | null;
  /** Non-empty groups only, in the order part → preset → computed. */
  children: RelatedGroup[];
}

const CHILD_GROUP_ORDER: RelationKind[] = ['part', 'preset', 'computed'];

export function buildRelations(cat: DocsCatalogue, e: WikiEntry): Relations {
  // A KingCAD layer's "parent" is the product whose table it reads (runtime link, no parentKey).
  const product = e.cad?.productKey ? (cat.byKey.get(e.cad.productKey) ?? null) : null;
  const parentEntry = product ?? parentOf(cat, e);
  const parent = parentEntry ? { kind: product ? ('cad' as const) : relationKind(e), entry: parentEntry } : null;
  const kids = childrenOf(cat, e.key);
  const children: RelatedGroup[] = CHILD_GROUP_ORDER.map((kind) => ({ kind, entries: kids.filter((k) => relationKind(k) === kind) })).filter(
    (g) => g.entries.length > 0
  );
  const cadKids = (e.cadLayers ?? []).map((c) => cat.byKey.get(c.key)).filter((x): x is WikiEntry => x !== undefined);
  if (cadKids.length > 0) children.push({ kind: 'cad', entries: cadKids });
  return { parent, children };
}

/**
 * »Samo splet« when KingCAD does not serve the layer; null otherwise. A desktop-only
 * entry never reaches the web tab (docsModel `desktopOnly`), so there is no desktop chip.
 */
export function surfaceChip(e: WikiEntry, parent: WikiEntry | null): string | null {
  return surfaceOf(e, parent) === 'web' ? docsCopy.surface.web : null;
}
